import { animated, useSpring } from "@react-spring/three";
import { useGLTF } from "@react-three/drei";
import React, { useContext, useEffect, useMemo } from "react";
import { DoubleSide, TextureLoader } from "three";
import {
  BoltClosePos,
  BoltOpenPosBack,
  BoltOpenPosFront,
  BoltOpenPosTop,
  Deg0,
  DoorDeg90CCW,
  DoorDeg90CW,
  HandleDeg45CW,
  Scale0,
  Scale1,
} from "../../utils/AnimaionSprings";
import CurrentContext from "../../utils/CurrentContext";
import useChangedTexture from "../../utils/customHooks/useChangedTexture";
import useTextureCustom from "../../utils/customHooks/useTextureCustom";
import {
  BlackPlastic,
  BoltMaterial,
  PaintMaterial,
} from "../../utils/finsishesData";
import { handleExportGLTF } from "../../utils/jsHelper";
import { LockMechanicalLarge, LockSecuramBacklit, LockSecuramProLogic } from "./model_locks";
import useIsMobile from "../../utils/customHooks/useIsMobile";

//region Default options
const defaultoptions = [
  {
    materialName: "body_paint",
    finishToApply: "bronzegloss",
  },
  {
    materialName: "securam_prologic",
    finishToApply: "Lock_securam_prologic",
  },
  {
    materialName: "mechanical_lock",
    finishToApply: "Lock_mechanical_large",
  },
];

const HandleUpdateTexture = (item, materials) => {
  useTextureCustom(materials[item.materialName], item.finishToApply);
};

export function VaultBeast({ animateInit, currentVariant, lockType }) {
  const isMobile = useIsMobile();
  const { nodes, materials, scene } = useGLTF(
    isMobile ? "models/vaultdoorbeast-m.glb" : "models/vaultdoorbeast.glb");
  const mobileModel = useGLTF("models/vaultdoorbeast-m.glb");

  if (isMobile) {
    return (
      <Model
        {...{
          nodes,
          materials,
          scene,
          animateInit,
          currentVariant,
          lockType,
          mobileProp: true,
        }}
      />
    );
  } else {
    return (
      <>
        <Model
          {...{
            nodes,
            materials,
            scene,
            animateInit,
            currentVariant,
            lockType,
          }}
        />
        <Model
          {...{
            nodes: mobileModel?.nodes,
            materials: mobileModel?.materials,
            scene: mobileModel?.scene,
            animateInit,
            currentVariant,
            lockType,
            visible: false,
            mobileProp: true,
          }}
        />
      </>
    );
  }
}

const Model = ({
  nodes,
  materials,
  scene,
  animateInit,
  currentVariant,
  lockType,
  visible = true,
  mobileProp = false,
}) => {
  const {
    setLoading,
    setIsARReady,
    setARModelSrc,
    setARUnicode,
    isAR,
    setIsAR,
    doorOpen,
  } = useContext(CurrentContext);


  //region animation springs
  const { rotation_Door_in } = useSpring({
    rotation_Door_in: doorOpen ? DoorDeg90CCW : Deg0,
    config: { duration: 2000 },
    delay: 1250,
  });
  //region animation springs
  const { rotation_Door_out } = useSpring({
    rotation_Door_out: doorOpen ? DoorDeg90CW : Deg0,
    config: { duration: 2000 },
    delay: 1250,
  });

  const { handle_Rotation1 } = useSpring({
    handle_Rotation1: doorOpen ? HandleDeg45CW : Deg0,
    config: { duration: 1000 },
    delay: doorOpen ? 0 : 3250,
  });

  const { handle_Rotation2 } = useSpring({
    handle_Rotation2: doorOpen ? Deg0 : HandleDeg45CW,
    config: { duration: 1000 },
    delay: doorOpen ? 3250 : 0,
  });

  const { handle_Scale1 } = useSpring({
    handle_Scale1: doorOpen ? Scale0 : Scale1,
    config: { duration: 1 },
    delay: 2250,
  });

  const { handle_Scale2 } = useSpring({
    handle_Scale2: doorOpen ? Scale1 : Scale0,
    config: { duration: 1 },
    delay: 2250,
  });

  const { position_Bolt_Front } = useSpring({
    position_Bolt_Front: doorOpen ? BoltOpenPosFront : BoltClosePos,
    config: { duration: 1000 },
    delay: doorOpen ? 3250 : 0,
  });

  const { position_Bolt_Back } = useSpring({
    position_Bolt_Back: doorOpen ? BoltOpenPosBack : BoltClosePos,
    config: { duration: 1000 },
    delay: doorOpen ? 3250 : 0,
  });

  const { position_Bolt_Top } = useSpring({
    position_Bolt_Top: doorOpen ? BoltOpenPosTop : BoltClosePos,
    config: { duration: 1000 },
    delay: doorOpen ? 3250 : 0,
  });

  useEffect(() => {
    if (isAR && mobileProp) {
      handleExportGLTF({
        scene,
        fileName: "vaultdoorbeast",
        setLoading,
      }).then(({ file, unicode } = {}) => {
        setARModelSrc(file);
        setIsARReady(true);
        setARUnicode(unicode);
      });
    }
    setIsAR(false);
  }, [isAR,mobileProp]);

  const loader = useMemo(() => new TextureLoader(), []);

  const decalDoorLogo = useMemo(
    () => loader.load("textures/decal_door_vaultdoorbeast.png"),
    []
  );
  defaultoptions.map((item) => {
    HandleUpdateTexture(item, materials);
  });

  const materialNames = {
    outer_materials: "body_paint",
    hardware_materials: [
      "wheel_handle_outer",
      "wheel_handle_door_inner",
      "mechanical_lock",
      "securam_prologic",
    ],
  };

  useChangedTexture(
    currentVariant,
    materials,
    materialNames,
    HandleUpdateTexture
  );
  useTextureCustom(PaintMaterial, currentVariant.outer);

  const decalOuterColor =
    currentVariant.hardware === "blackchromehardware"
      ? "#787473"
      : currentVariant.hardware === "chromehardware"
      ? "#D1D0D0"
      : currentVariant.hardware === "brasshardware"
      ? "#B59660"
      : "#787473";

  return (
    <group
      dispose={null}
      scale={[currentVariant.Hinge === "left" ? 1 : -1, 1, 1]}
      visible={visible}>
      {/* <animated.group position={[-0.435, 0, 0.133]} rotation={rotation_Door}> */}
      <animated.group
        position={
          currentVariant.Swing === "in"
            ? [-0.4, 0.025, -0.025]
            : [-0.435, 0.025, 0.133]
        }
        rotation={
          currentVariant.Swing === "in" ? rotation_Door_in : rotation_Door_out
        }
      >
        <group
          position={
            currentVariant.Swing === "in" ? [-0.035, 0, 0.158] : [0, 0, 0]
          }
        >
          <animated.mesh
            castShadow
            receiveShadow
            geometry={nodes.bolt_back.geometry}
            material={BoltMaterial}
            position={position_Bolt_Front}
          />
          <animated.mesh
            castShadow
            receiveShadow
            geometry={nodes.bolt_front.geometry}
            material={BoltMaterial}
            position={position_Bolt_Back}
          />
          <animated.mesh
            castShadow
            receiveShadow
            geometry={nodes.bolts_top.geometry}
            material={BoltMaterial}
            position={position_Bolt_Top}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.bolt_housing.geometry}
            material={BlackPlastic}
            // position={[0.431, 1.111, -0.09]}
          />

          <mesh
            castShadow
            receiveShadow
            geometry={nodes.handles_1.geometry}
            // material={materials.YHinges_Chrome}
            material={materials.wheel_handle_outer}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.handles_2.geometry}
            material={materials.handles}
          />

          <mesh
            castShadow
            receiveShadow
            geometry={nodes.hinges_1.geometry}
            // material={materials.hinges}
            material={PaintMaterial}
            position={
              currentVariant.Swing === "in" ? [0.035, 0, -0.158] : [0, 0, 0]
            }
          />

          <mesh
            castShadow
            receiveShadow
            geometry={nodes.hinges_2.geometry}
            // material={materials.hinges}
            material={materials.wheel_handle_outer}
            position={
              currentVariant.Swing === "in" ? [0.035, 0, -0.158] : [0, 0, 0]
            }
          />

          {/* Lock */}
          <group position={[0.435, 1.2975, -0.017]}>
            <LockMechanicalLarge
              material={materials.mechanical_lock}
              scale={lockType === "mechanicallock" ? [1, 1, 1] : [0, 0, 0]}
              decalOuterColor={decalOuterColor}
            />
            {/* <LockSecuramProLogic
              material={materials.securam_prologic}
              visible={lockType !== "mechanicallock" ? [1, 1, 1] : [0, 0, 0]}
            /> */}
            <LockSecuramBacklit
                material={materials.wheel_handle_outer}
                visible={lockType !== "mechanicallock" ? [1, 1, 1] : [0, 0, 0]}
              />
          </group>
          {/* Door Exterior */}
          <group>
            <mesh
              castShadow
              receiveShadow
              geometry={nodes.outer_door.geometry}
              // material={materials.body_paint}
              material={PaintMaterial}
            />
            <mesh
              position={[0.43, 1, -0.01]}
              scale={[currentVariant.Hinge === "left" ? 4.4 : -4.4, 4.4, 1]}
            >
              <planeGeometry args={[0.7, 0.7]} />
              <meshBasicMaterial
                color={decalOuterColor}
                map={decalDoorLogo}
                transparent={true}
              />
            </mesh>
          </group>

          <mesh
            castShadow
            receiveShadow
            geometry={nodes.pockets_base_1.geometry}
            material={materials.Inner_Door_Pocket1}
          />
          <mesh
            castShadow
            geometry={nodes.pockets_base_2.geometry}
            material={materials.pockets_base}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.pockets_base_3.geometry}
            material={materials.Inside_Decal_Out}
            position={[0.54, 0, 0]}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.pockets_base_4.geometry}
            material={materials.Wall_Bolt_Housing}
          />

          <animated.mesh
            rotation={handle_Rotation1}
            scale={handle_Scale1}
            castShadow
            receiveShadow
            geometry={nodes.wheel_handle_door_inner.geometry}
            material={materials.wheel_handle_door_inner}
            position={[0.431, 1.13, -0.2]}
          />
          <animated.mesh
            rotation={handle_Rotation2}
            scale={handle_Scale2}
            castShadow
            receiveShadow
            geometry={nodes.wheel_handle_door_inner.geometry}
            material={materials.wheel_handle_door_inner}
            position={[0.431, 1.13, -0.2]}
          />

          <animated.group
            position={[0.432, 0.983, 0.044]}
            rotation={handle_Rotation1}
            scale={handle_Scale1}
          >
            <mesh
              position={[0, 0, 0.005]}
              castShadow
              receiveShadow
              geometry={nodes.wheel_handle_outer_1.geometry}
              material={materials.Handle_Plane}
            />
            <mesh
              castShadow
              receiveShadow
              geometry={nodes.wheel_handle_outer_2.geometry}
              material={materials.wheel_handle_outer}
            />
            <mesh
              castShadow
              receiveShadow
              geometry={nodes.wheel_handle_outer_3.geometry}
              material={materials.Wheel_Decal}
            />
          </animated.group>
          <animated.group
            position={[0.432, 0.983, 0.044]}
            rotation={handle_Rotation2}
            scale={handle_Scale2}
          >
            <mesh
              position={[0, 0, 0.005]}
              castShadow
              receiveShadow
              geometry={nodes.wheel_handle_outer_1.geometry}
              material={materials.Handle_Plane}
            />
            <mesh
              castShadow
              receiveShadow
              geometry={nodes.wheel_handle_outer_2.geometry}
              material={materials.wheel_handle_outer}
            />
            <mesh
              castShadow
              receiveShadow
              geometry={nodes.wheel_handle_outer_3.geometry}
              material={materials.Wheel_Decal}
            />
          </animated.group>
        </group>
      </animated.group>

      <mesh
        castShadow
        geometry={nodes.door_frame_rubber.geometry}
        material={materials.door_frame_rubber}
      />
      <mesh
        castShadow
        geometry={nodes.outer_body.geometry}
        // material={materials.body_paint}
        material={PaintMaterial}
      />
      <mesh
        castShadow
        geometry={nodes.screws.geometry}
        material={materials.screws}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.body_housing.geometry}
        material={materials.body_housing}
      />
    </group>
  );
}

//useGLTF.preload("models/vaultdoorbeast.glb");
